/**
 * Profile Page Component
 * Menampilkan data user dan form ganti password
 */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import ChangePasswordForm from './ChangePasswordForm';

const ProfilePage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [showChangePassword, setShowChangePassword] = useState(false);

  if (!user) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  // Role badge color
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'admin':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'kasir':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Jika menampilkan form ganti password
  if (showChangePassword) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => setShowChangePassword(false)}
            className="mb-4 text-blue-600 hover:text-blue-800 flex items-center gap-2"
          >
            ← Kembali ke Profile
          </button>
          
          <ChangePasswordForm
            onSuccess={() => {
              // Auto logout sudah di-handle di component
            }}
            onCancel={() => setShowChangePassword(false)}
          />
        </div>
      </div>
    );
  }

  // Tampilan profile
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Profile Saya</h1>

        {/* Profile Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{user.full_name}</h2>
              <p className="text-gray-600">@{user.username}</p>
            </div>
            <div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getRoleBadgeColor(user.role)}`}>
                {user.role}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {/* Username */}
            <div className="border-b border-gray-200 pb-3">
              <label className="text-sm font-medium text-gray-600 block mb-1">Username</label>
              <p className="text-gray-800 font-medium">{user.username}</p>
            </div>

            {/* Full Name */}
            <div className="border-b border-gray-200 pb-3">
              <label className="text-sm font-medium text-gray-600 block mb-1">Nama Lengkap</label>
              <p className="text-gray-800 font-medium">{user.full_name}</p>
            </div>

            {/* Role */}
            <div className="border-b border-gray-200 pb-3">
              <label className="text-sm font-medium text-gray-600 block mb-1">Role</label>
              <p className="text-gray-800 font-medium">{user.role}</p>
            </div>

            {/* Branch */}
            {user.branch_id && (
              <div className="border-b border-gray-200 pb-3">
                <label className="text-sm font-medium text-gray-600 block mb-1">Cabang</label>
                <p className="text-gray-800 font-medium">Cabang #{user.branch_id}</p>
              </div>
            )}

            {/* Status */}
            <div className="border-b border-gray-200 pb-3">
              <label className="text-sm font-medium text-gray-600 block mb-1">Status</label>
              <span className={`inline-flex items-center px-2 py-1 rounded text-sm font-medium
                ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
              `}>
                {user.is_active ? '✓ Aktif' : '✗ Nonaktif'}
              </span>
            </div>

            {/* Last Login */}
            {user.last_login && (
              <div className="border-b border-gray-200 pb-3">
                <label className="text-sm font-medium text-gray-600 block mb-1">Login Terakhir</label>
                <p className="text-gray-800">
                  {new Date(user.last_login).toLocaleString('id-ID', {
                    dateStyle: 'long',
                    timeStyle: 'short'
                  })}
                </p>
              </div>
            )}

            {/* Member Since */}
            <div className="pb-3">
              <label className="text-sm font-medium text-gray-600 block mb-1">Bergabung Sejak</label>
              <p className="text-gray-800">
                {new Date(user.created_at).toLocaleString('id-ID', {
                  dateStyle: 'long'
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Change Password Button */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">Keamanan Akun</h3>
          <p className="text-gray-600 text-sm mb-4">
            Untuk keamanan akun Anda, ganti password secara berkala.
          </p>
          <button
            onClick={() => setShowChangePassword(true)}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors"
          >
            🔐 Ganti Password
          </button>
        </div>

        {/* Info Box untuk Kasir */}
        {user.role === 'kasir' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              ℹ️ <strong>Informasi:</strong> Sebagai kasir, Anda hanya dapat mengganti password. 
              Untuk mengubah data pribadi seperti nama atau username, hubungi admin atau owner.
            </p>
          </div>
        )}

        {/* Info Box untuk Admin */}
        {user.role === 'admin' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              ℹ️ <strong>Informasi:</strong> Anda dapat mengelola user kasir di cabang Anda. 
              Untuk mengubah data pribadi Anda sendiri, hubungi owner.
            </p>
          </div>
        )}

        {/* Info Box untuk Owner */}
        {user.role === 'owner' && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-800">
              ℹ️ <strong>Owner Access:</strong> Anda memiliki akses penuh ke semua fitur sistem. 
              Kelola user, cabang, dan pengaturan sistem melalui menu navigasi.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
